import React, {useEffect, useMemo, useState, useCallback, useContext} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useTradeJournals } from '../../hooks/useTradeJournals';
import { useTradeJournalsQuery } from '../../hooks/useTradeStationQueries';
import { refreshTradeJournalsCache } from '../../utils/tradeJournalCache';
import { SortDown, SortUp, Trash, PencilSquare } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useJournalModal } from '../../contexts/JournalModalContext';
import { TradingContext } from '../../contexts/TradingContext';
import { AuthContext } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import { isDashboardPath, navigateToTradeWithSymbol, canOpenTradeWithSymbol } from '../../utils/tradeNavigation';
import TradeJournalDesktopCard from './TradeJournalDesktopCard';
import TradeJournalViewToggle from './TradeJournalViewToggle';
import { DESKTOP_VIEW_STORAGE_KEY, getJournalPnl, journalPnlTone, journalPurpleBorderStyle } from './journalListUtils';
import { getAccountDisplayLabel } from '../../utils/accountNickname';

const readStoredDesktopView = () => {
  try {
    const stored = localStorage.getItem(DESKTOP_VIEW_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'cards';
  } catch (_) {
    return 'cards';
  }
};

// OPTIMIZED: Cache currency formatter at module level
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const VISIBLE_PILLS_COLLAPSED = 3;

const TradeJournalList = () => {
  const { colors: themeColors } = useTheme();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768, 1024);
  const onDashboard = isDashboardPath(location.pathname);
  const { accounts, selectedAccount } = useContext(TradingContext);
  const tickerLinksToTrade = onDashboard && canOpenTradeWithSymbol(user, selectedAccount);

  const handleDashboardTickerClick = useCallback(
    (ticker, ev) => {
      ev?.stopPropagation?.();
      navigateToTradeWithSymbol(navigate, ticker, user, selectedAccount);
    },
    [navigate, selectedAccount, user]
  );
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [desktopListView, setDesktopListView] = useState(readStoredDesktopView);
  const queryClient = useQueryClient();
  const tradeJournalsQuery = useTradeJournalsQuery(!!user?.token);
  const tradeJournals = useMemo(() => tradeJournalsQuery.data || [], [tradeJournalsQuery.data]);
  const { deleteTradeJournal } = useTradeJournals();
  const { openNewJournal, openEditJournal } = useJournalModal();

  const refreshTradeJournals = useCallback(() => {
    refreshTradeJournalsCache(queryClient);
  }, [queryClient]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDelete = useCallback(async (id) => {
    await deleteTradeJournal(id);
    refreshTradeJournals();
    setDeleteConfirmId(null);
  }, [deleteTradeJournal, refreshTradeJournals]);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDesktopViewChange = useCallback((view) => {
    setDesktopListView(view);
    try {
      localStorage.setItem(DESKTOP_VIEW_STORAGE_KEY, view);
    } catch (_) {}
  }, []);

  const openJournal = useCallback((journal) => {
    openEditJournal(journal);
  }, [openEditJournal]);

  useEffect(() => {
    const onCreated = () => refreshTradeJournals();
    const onUpdated = () => refreshTradeJournals();
    window.addEventListener('tradeJournal:created', onCreated);
    window.addEventListener('tradeJournal:updated', onUpdated);
    return () => {
      window.removeEventListener('tradeJournal:created', onCreated);
      window.removeEventListener('tradeJournal:updated', onUpdated);
    };
  }, [refreshTradeJournals]);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape') setDeleteConfirmId(null);
    };
    if (deleteConfirmId) {
      document.addEventListener('keydown', onEscape);
      return () => document.removeEventListener('keydown', onEscape);
    }
  }, [deleteConfirmId]);

  const sortedJournals = useMemo(() => {
    if (!Array.isArray(tradeJournals)) return [];
    const normalizeDate = (value) => {
      if (!value) return 0;
      const t = Date.parse(value);
      return Number.isNaN(t) ? 0 : t;
    };
    return [...tradeJournals].sort((a, b) => {
      const aEntry = a?.entry || {};
      const bEntry = b?.entry || {};
      let aValue;
      let bValue;
      if (sortField === 'createdAt') {
        aValue = normalizeDate(aEntry.createdAt) || a.id;
        bValue = normalizeDate(bEntry.createdAt) || b.id;
      } else if (sortField === 'id') {
        aValue = a.id;
        bValue = b.id;
      } else {
        aValue = aEntry?.values?.[sortField] ?? aEntry?.[sortField] ?? '';
        bValue = bEntry?.values?.[sortField] ?? bEntry?.[sortField] ?? '';
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [tradeJournals, sortField, sortDirection]);

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <SortUp size={12} /> : <SortDown size={12} />;
  };

  const formatJournalDate = useCallback((value) => {
    try {
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return '';
      const hours = dt.getHours();
      const minutes = dt.getMinutes();
      const h12 = hours % 12 === 0 ? 12 : hours % 12;
      const mm = String(minutes).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const time = `${h12}:${mm}${ampm}`;
      const days = ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dow = days[dt.getDay()];
      const day = dt.getDate();
      const suffix = (d) => {
        const v = d % 100;
        if (v >= 11 && v <= 13) return 'th';
        switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
      };
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return `${time} ${dow} ${day}${suffix(day)}, ${month}, ${year}`;
    } catch (_) { return ''; }
  }, []);

  // Compact date for mobile card: "9:31AM Mon. 18th, Sept"
  const formatCardDate = useCallback((value) => {
    try {
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return '';
      const hours = dt.getHours();
      const minutes = dt.getMinutes();
      const h12 = hours % 12 === 0 ? 12 : hours % 12;
      const mm = String(minutes).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const time = `${h12}:${mm}${ampm}`;
      const days = ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      const dow = days[dt.getDay()];
      const day = dt.getDate();
      const suffix = (d) => {
        const v = d % 100;
        if (v >= 11 && v <= 13) return 'th';
        switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
      };
      const month = months[dt.getMonth()];
      return `${time} ${dow} ${day}${suffix(day)}, ${month}`;
    } catch (_) { return ''; }
  }, []);

  const formatCurrency = useCallback((value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return currencyFormatter.format(num);
  }, []);

  const getMobileDateParts = (value) => {
    try {
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return { top: '', bottom: '' };
      const hours = dt.getHours();
      const minutes = dt.getMinutes();
      const h12 = hours % 12 === 0 ? 12 : hours % 12;
      const mm = String(minutes).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const time = `${h12}:${mm}${ampm}`;
      const days = ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dow = days[dt.getDay()];
      const day = dt.getDate();
      const month = months[dt.getMonth()];
      const year = dt.getFullYear().toString().slice(-2);
      return { top: `${time} ${dow}`, bottom: `${month} ${day}, ${year}` };
    } catch (_) { return { top: '', bottom: '' }; }
  };

  const getDesktopDateParts = (value) => {
    try {
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return { top: '', bottom: '' };
      const hours = dt.getHours();
      const minutes = dt.getMinutes();
      const h12 = hours % 12 === 0 ? 12 : hours % 12;
      const mm = String(minutes).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const time = `${h12}:${mm}${ampm}`;
      const days = ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dow = days[dt.getDay()];
      const day = dt.getDate();
      const suffix = (d) => {
        const v = d % 100;
        if (v >= 11 && v <= 13) return 'th';
        switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
      };
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return { top: `${time} ${dow}`, bottom: `${month} ${day}${suffix(day)}, ${year}` };
    } catch (_) { return { top: '', bottom: '' }; }
  };

  const getTickerFromJournal = (j) => {
    const e = j.entry || {};
    const values = e.values || {};
    const fields = Array.isArray(e.template?.fields) ? e.template.fields : [];
    for (const field of fields) {
      const key = field.key || field.label;
      const keyLower = (key || '').toLowerCase();
      if ((keyLower.includes('ticker') || keyLower.includes('symbol')) && field.type === 'text') {
        const ticker = values[key] || e[key];
        if (ticker && String(ticker).trim()) return String(ticker).trim().toUpperCase();
      }
    }
    for (const [key, value] of Object.entries(values)) {
      const keyLower = key.toLowerCase();
      if ((keyLower.includes('ticker') || keyLower.includes('symbol')) && value && String(value).trim()) {
        return String(value).trim().toUpperCase();
      }
    }
    return '—';
  };

  const getAccountName = (accountId) => {
    if (!accountId) return '—';
    const accountDefaults = user?.accountSettings || user?.accountDefaults;
    const account = accounts?.find(acc => acc.AccountID === accountId);
    return getAccountDisplayLabel(account || accountId, accountDefaults);
  };

  const getJournalChips = useCallback((j) => {
    const e = j.entry || {};
    const values = e.values || {};
    const fields = Array.isArray(e.template?.fields) ? e.template.fields : [];
    const byPosition = fields
      .map((f, i) => ({ ...f, __i: i }))
      .sort((a, b) => ((a.position ?? a.__i) - (b.position ?? b.__i)));
    const getKey = (f) => f?.key || f?.label;
    const isTickerField = (f) => {
      const key = getKey(f);
      const keyLower = (key || '').toLowerCase();
      return keyLower.includes('ticker') || keyLower.includes('symbol');
    };
    const journalInfoFields = byPosition.filter(f => f.type !== 'datetime' && !isTickerField(f));
    return journalInfoFields.map(f => {
      const raw = values[getKey(f)] ?? e[getKey(f)];
      let v = raw;
      if (f.type === 'datetime') v = formatJournalDate(raw);
      if (f.type === 'checkbox') v = Boolean(raw) ? 'Yes' : 'No';
      if (f.type === 'currency') v = raw === '' ? '' : formatCurrency(raw);
      if (Array.isArray(v)) v = v.join(', ');
      return { label: f.label || getKey(f), value: (v ?? '') !== '' ? String(v) : '—' };
    });
  }, [formatJournalDate, formatCurrency]);

  const renderMobileCard = (j) => {
    const e = j.entry || {};
    const values = e.values || {};
    const fields = Array.isArray(e.template?.fields) ? e.template.fields : [];
    const datetimeField = fields.find(f => f.type === 'datetime');
    const dateKey = datetimeField ? (datetimeField.key || datetimeField.label) : null;
    const dateRaw = (dateKey ? (values[dateKey] ?? e[dateKey]) : null) || e.createdAt || null;

    const ticker = getTickerFromJournal(j);
    const accountDisplay = getAccountName(j.account_id);
    const chips = getJournalChips(j);
    const pnl = getJournalPnl(j);
    const pnlTone = journalPnlTone(themeColors, pnl?.numeric);
    const expanded = expandedIds.has(j.id);
    const visibleChips = expanded ? chips : chips.slice(0, VISIBLE_PILLS_COLLAPSED);
    const hiddenCount = Math.max(0, chips.length - VISIBLE_PILLS_COLLAPSED);

    const cardDateStyle = {
      fontSize: '0.68rem',
      color: themeColors.textMuted,
      fontWeight: 400,
    };
    const pillStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 7px',
      borderRadius: 6,
      fontSize: '0.72rem',
      ...journalPurpleBorderStyle(themeColors, themeColors.bgSecondary),
    };

    const canToggleChips = chips.length > VISIBLE_PILLS_COLLAPSED;

    return (
      <div
        key={j.id}
        role={canToggleChips ? 'button' : undefined}
        tabIndex={canToggleChips ? 0 : undefined}
        aria-expanded={canToggleChips ? expanded : undefined}
        aria-label={canToggleChips ? (expanded ? 'Collapse journal details' : 'Expand journal details') : undefined}
        onClick={() => {
          if (canToggleChips) toggleExpanded(j.id);
        }}
        onKeyDown={(ev) => {
          if (!canToggleChips) return;
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            toggleExpanded(j.id);
          }
        }}
        style={{
          backgroundColor: 'transparent',
          borderRadius: 8,
          ...journalPurpleBorderStyle(themeColors, 'transparent'),
          padding: '10px 12px',
          cursor: canToggleChips ? 'pointer' : 'default',
        }}
      >
        <div style={{ paddingBottom: 10, borderBottom: `1px solid ${themeColors.border}`, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              {tickerLinksToTrade && ticker ? (
                <button
                  type="button"
                  onClick={(ev) => handleDashboardTickerClick(ticker, ev)}
                  title={`Open ${ticker} on Trade`}
                  style={{
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: themeColors.primary,
                    padding: '2px 6px',
                    background: `${themeColors.primary}18`,
                    borderRadius: 4,
                    flexShrink: 0,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {ticker}
                </button>
              ) : (
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: themeColors.primary,
                    padding: '2px 6px',
                    background: `${themeColors.primary}18`,
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                >
                  {ticker}
                </div>
              )}
              <div style={cardDateStyle}>
                {formatCardDate(dateRaw)}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {pnl && (
                <span
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: pnlTone.color,
                    whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                  }}
                >
                  {pnl.formatted}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); openEditJournal(j); }}
                style={{
                  width: 24,
                  height: 24,
                  border: 'none',
                  background: 'none',
                  color: themeColors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  opacity: 0.75,
                }}
                title="Edit journal"
              >
                <PencilSquare size={14} />
              </button>
              <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); setDeleteConfirmId(j.id); }}
                style={{
                  width: 24,
                  height: 24,
                  border: 'none',
                  background: 'none',
                  color: themeColors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  opacity: 0.75,
                }}
                title="Delete journal"
              >
                <Trash size={14} />
              </button>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.68rem', color: themeColors.textMuted, letterSpacing: '0.03em' }}>
            Account: {accountDisplay}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {visibleChips.map((c, i) => (
            <span key={i} style={pillStyle}>
              <span style={{ color: themeColors.textMuted }}>{c.label}:</span>
              <span style={{ color: themeColors.text }}>{c.value}</span>
            </span>
          ))}
          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); toggleExpanded(j.id); }}
              style={{
                fontSize: '0.72rem',
                color: themeColors.textMuted,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '3px 6px',
                flexShrink: 0,
              }}
            >
              {hiddenCount} more
            </button>
          )}
          {expanded && chips.length > VISIBLE_PILLS_COLLAPSED && (
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); toggleExpanded(j.id); }}
              style={{
                fontSize: '0.72rem',
                color: themeColors.textMuted,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '3px 6px',
                flexShrink: 0,
              }}
            >
              Show less
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCompactRow = (j) => {
    const e = j.entry || {};
    const values = e.values || {};
    const fields = Array.isArray(e.template?.fields) ? e.template.fields : [];

    const byPosition = fields
      .map((f, i) => ({ ...f, __i: i }))
      .sort((a, b) => ((a.position ?? a.__i) - (b.position ?? b.__i)));

    const datetimeField = byPosition.find(f => f.type === 'datetime');
    const dateKey = datetimeField ? (datetimeField.key || datetimeField.label) : null;
    const dateRaw = (dateKey ? (values[dateKey] ?? e[dateKey]) : null) || e.createdAt || null;

    const ticker = getTickerFromJournal(j);
    const accountName = getAccountName(j.account_id);
    const pnl = getJournalPnl(j);
    const pnlTone = journalPnlTone(themeColors, pnl?.numeric);

    const getKey = (f) => f?.key || f?.label;
    const isTickerField = (f) => {
      const key = getKey(f);
      const keyLower = (key || '').toLowerCase();
      return keyLower.includes('ticker') || keyLower.includes('symbol');
    };
    const journalInfoFields = byPosition.filter(f => f.type !== 'datetime' && !isTickerField(f));
    const chips = journalInfoFields.map(f => {
      const raw = values[getKey(f)] ?? e[getKey(f)];
      let v = raw;
      if (f.type === 'datetime') v = formatJournalDate(raw);
      if (f.type === 'checkbox') v = Boolean(raw) ? 'Yes' : 'No';
      if (f.type === 'currency') v = raw === '' ? '' : formatCurrency(raw);
      if (Array.isArray(v)) v = v.join(', ');
      const fieldKey = f.label || getKey(f);
      return { label: fieldKey, value: (v ?? '') !== '' ? String(v) : '—' };
    }).filter((chip) => !pnl || chip.label !== pnl.label);

    const p = isMobile ? getMobileDateParts(dateRaw) : getDesktopDateParts(dateRaw);
    return (
      <>
        <td style={{ whiteSpace: 'nowrap', width: isMobile ? '80px' : '110px', backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border }}>
          <div style={{ lineHeight: 1.1 }}>
            <small style={{ opacity: 0.9 }}>{p.top}</small>
            <br />
            <small style={{ opacity: 0.9 }}>{p.bottom}</small>
          </div>
        </td>
        <td style={{ backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: isMobile ? '60px' : '80px' }}>
          {tickerLinksToTrade && ticker ? (
            <button
              type="button"
              onClick={(ev) => handleDashboardTickerClick(ticker, ev)}
              title={`Open ${ticker} on Trade`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: themeColors.primary,
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              {ticker}
            </button>
          ) : (
            ticker
          )}
        </td>
        <td style={{ backgroundColor: themeColors.bgPrimary, color: pnlTone.color, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: '100px', fontWeight: 700 }}>
          {pnl?.formatted || '—'}
        </td>
        <td style={{ backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: isMobile ? '70px' : '90px' }}>
          {accountName}
        </td>
        <td style={{ backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border, width: 'auto', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', paddingBottom: '4px', width: '100%' }}>
            {chips.map((c, i) => (
              <span key={i} style={{
                display: 'inline-flex', gap: 4, alignItems: 'center',
                borderRadius: 10, padding: '2px 6px', fontSize: '0.8rem',
                flexShrink: 0, whiteSpace: 'nowrap',
                ...journalPurpleBorderStyle(themeColors, themeColors.bgSecondary),
              }}>
                <span style={{ color: themeColors.textLight }}>{c.label}:</span>
                <span style={{ color: themeColors.text }}>{c.value}</span>
              </span>
            ))}
          </div>
        </td>
        <td style={{ width: '50px', textAlign: 'center', backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border, position: 'sticky', right: 0, zIndex: 10 }}>
          <Trash role="button" onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(j.id); }} />
        </td>
      </>
    );
  };

  const renderDeleteModal = () => {
    if (!deleteConfirmId) return null;
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20,
        }}
        onClick={() => setDeleteConfirmId(null)}
      >
        <div
          style={{
            background: themeColors.bgSecondary || '#141820',
            border: `1px solid ${themeColors.border || '#334155'}`,
            borderRadius: 12, padding: 20, maxWidth: 320, width: '100%',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="delete-modal-title" style={{ fontSize: '1rem', fontWeight: 600, color: themeColors.text, margin: '0 0 8px 0' }}>
            Delete journal?
          </h3>
          <p style={{ fontSize: '0.875rem', color: themeColors.textLight || '#94A3B8', marginBottom: 20, lineHeight: 1.5 }}>
            This cannot be undone. The journal entry will be permanently removed.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(null)}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: '0.875rem', fontWeight: 500,
                cursor: 'pointer', border: 'none', background: 'transparent',
                color: themeColors.textLight || '#94A3B8',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleDelete(deleteConfirmId)}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: '0.875rem', fontWeight: 500,
                cursor: 'pointer', border: 'none',
                background: 'rgba(247, 50, 99, 0.15)', color: '#F87171',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDesktopToolbar = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '12px 14px',
        borderBottom: `1px solid ${themeColors.border}`,
        backgroundColor: themeColors.bgSecondary,
      }}
    >
      <button
        type="button"
        onClick={() => handleSort('createdAt')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          borderRadius: 6,
          border: `1px solid ${themeColors.border}`,
          background: themeColors.bgPrimary,
          color: themeColors.text,
          fontSize: '0.8rem',
          cursor: 'pointer',
        }}
      >
        Sort by date {renderSortIcon('createdAt')}
      </button>
      <TradeJournalViewToggle value={desktopListView} onChange={handleDesktopViewChange} />
    </div>
  );

  return (
    <div style={{ height: '100%', position: 'relative', overflowX: isMobile ? 'hidden' : 'auto', overflowY: 'auto' }}>
      {isMobile ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sortedJournals.map((j) => renderMobileCard(j))}
          </div>
          {renderDeleteModal()}
        </>
      ) : (
        <>
          {renderDesktopToolbar()}
          {desktopListView === 'cards' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}>
              {sortedJournals.length === 0 ? (
                <p style={{ margin: 0, fontSize: '0.875rem', color: themeColors.textMuted, textAlign: 'center', padding: '24px 12px' }}>
                  No journal entries yet. Create one to start tracking your trades.
                </p>
              ) : (
                sortedJournals.map((j) => (
                  <TradeJournalDesktopCard
                    key={j.id}
                    journal={j}
                    accountName={getAccountName(j.account_id)}
                    formatJournalDate={formatJournalDate}
                    onOpen={openJournal}
                    onEdit={openEditJournal}
                    onDeleteRequest={setDeleteConfirmId}
                    onTickerClick={tickerLinksToTrade ? handleDashboardTickerClick : undefined}
                  />
                ))
              )}
            </div>
          ) : (
            <Table bordered hover size="sm" className="positions-table" style={{ fontSize: '0.8rem', margin: 0, tableLayout: 'auto', minWidth: '100%', backgroundColor: themeColors.bgPrimary, color: themeColors.text }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer', whiteSpace: 'nowrap', width: '110px', backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border }}>
                    Date {renderSortIcon('createdAt')}
                  </th>
                  <th style={{ backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: '80px' }}>Ticker</th>
                  <th style={{ backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: '100px' }}>P&amp;L</th>
                  <th style={{ backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border, whiteSpace: 'nowrap', padding: '8px', width: '90px' }}>Account</th>
                  <th style={{ backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border, width: 'auto' }}>Journal Info</th>
                  <th style={{ width: '50px', backgroundColor: themeColors.bgSecondary, color: themeColors.text, borderColor: themeColors.border, position: 'sticky', right: 0, zIndex: 10 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedJournals.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => openJournal(j)}
                    style={{ cursor: 'pointer', backgroundColor: themeColors.bgPrimary, color: themeColors.text, borderColor: themeColors.border }}
                  >
                    {renderCompactRow(j)}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {renderDeleteModal()}
        </>
      )}
    </div>
  );
};

export default TradeJournalList;
